import { useState } from 'react';
import ReactPlayer from 'react-player';

import { HEADER_HEIGHT } from '@components/Header/HeaderStyles';
import Player from '@components/Player/Player';
import Skeleton from '@lib/core/src/components/Skeleton';
import palette from '@lib/core/src/theme/palette';
import { Drawer, Stack } from '@mui/material';

import { CalendarMeetingDto } from '@/api/generated';
import {
  DRAWER_WIDTH_LARGE,
  DRAWER_WIDTH_SMALL,
  TranscriptStack,
  MediaStack,
  MeetingMedia,
  DRAWER_WIDTH_MOBILE
} from '@/components/Meeting/MeetingStyles';
import Transcript from '@/components/Meeting/Transcript/Transcript';
import {
  MeetingTabsContent,
  MeetingTabsContentContainer
} from '@/components/Meeting/components/MeetingData/MeetingDataStyles';
import Tabs from '@/components/Meeting/components/MeetingData/Tabs';
import MeetingMyTasks from '@/components/Meeting/components/MeetingMyTasks';

interface MeetingDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  sharedMeeting: boolean;
  meeting: CalendarMeetingDto | undefined;
  onTimeClicked: (time: number | undefined) => void;
  meetingRecordingUrl: string | undefined;
  meetingTranscriptUrl: string | undefined;
  reactPlayerRef: React.RefObject<ReactPlayer>;
  transcriptContainerRef: React.RefObject<HTMLUListElement>;
  setVideoParts: (videoParts: Record<string, string>) => void;
  onVideoProgress: (playedSeconds: number) => void;
  onVideoReady: () => void;
  markedId: string;
  setIsScrolling: React.Dispatch<React.SetStateAction<boolean>>;
}

const MeetingDrawer = ({
  open,
  setOpen,
  sharedMeeting = false,
  meeting,
  onTimeClicked,
  meetingRecordingUrl,
  meetingTranscriptUrl,
  reactPlayerRef,
  transcriptContainerRef,
  setVideoParts,
  onVideoProgress,
  onVideoReady,
  markedId,
  setIsScrolling
}: MeetingDrawerProps) => {
  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  return (
    <Drawer
      open={open}
      anchor="right"
      variant="persistent"
      slotProps={{
        paper: {
          sx: {
            backgroundColor: palette.globalBackground,
            width: { lg: DRAWER_WIDTH_LARGE, md: DRAWER_WIDTH_SMALL, xs: DRAWER_WIDTH_MOBILE },
            borderLeft: { lg: `1px solid ${palette.grey[1200]}` },
            paddingTop: '2vh',
            top: HEADER_HEIGHT,
            height: `calc(100% - ${HEADER_HEIGHT})`
          }
        }
      }}
    >
      <Stack
        sx={{
          px: {
            xs: '1vw',
            md: '1vw',
            lg: '2vw'
          },
          height: '100%'
        }}
      >
        <MeetingTabsContentContainer>
          <Tabs
            tabIndex={tabIndex}
            onTabChange={handleTabChange}
            sharedMeeting={sharedMeeting}
            onClose={() => setOpen(false)}
          />
          <MeetingTabsContent>
            {tabIndex === 0 && (
              <MeetingMedia>
                <MediaStack>
                  {meetingRecordingUrl ? (
                    <Player
                      recordingUrl={meetingRecordingUrl}
                      reactPlayerRef={reactPlayerRef}
                      onVideoProgress={onVideoProgress}
                      onVideoReady={onVideoReady}
                    />
                  ) : (
                    <Skeleton count={1} />
                  )}
                </MediaStack>
                <TranscriptStack>
                  <Transcript
                    meetingTranscriptUrl={meetingTranscriptUrl}
                    transcriptContainerRef={transcriptContainerRef}
                    setVideoParts={setVideoParts}
                    onTimeClicked={onTimeClicked}
                    markedId={markedId}
                    setIsScrolling={setIsScrolling}
                  />
                </TranscriptStack>
              </MeetingMedia>
            )}

            {tabIndex === 1 && <MeetingMyTasks meeting={meeting} />}
          </MeetingTabsContent>
        </MeetingTabsContentContainer>
      </Stack>
    </Drawer>
  );
};

export default MeetingDrawer;
